import { Controller } from "@hotwired/stimulus"

// A DaisyUI dropdown with a real <button> as its trigger. Opening stays
// CSS-only (.dropdown:focus-within); this keeps aria-expanded in step with
// the focus, closes on Escape and makes a click work in Safari, which does
// not focus a clicked button and so never opens the menu on its own.
//
// Markup (PanelNavigationHelper#dropdown_attrs & co.):
//
//   .dropdown data-controller="dropdown" data-action="focusin->dropdown#opened focusout->dropdown#closed keydown.esc->dropdown#close"
//     button type="button" aria-haspopup="menu" aria-expanded="false" data-dropdown-target="trigger" data-action="mousedown->dropdown#remember click->dropdown#toggle"
//     ul.dropdown-content role="menu" tabindex="-1" data-dropdown-target="menu"
//
// The list keeps a tabindex of -1: Safari only hands a clicked element the
// focus when it has one, and the menu must stay open through a click on an
// item; -1 keeps it out of the Tab order, so Tab goes trigger -> first item.
export default class extends Controller {
  static targets = ["trigger", "menu"]

  connect() {
    if (this.hasMenuTarget) {
      this.menuTarget.id ||= `dropdown-${Math.random().toString(36).slice(2, 8)}`
      this.triggerTarget.setAttribute("aria-controls", this.menuTarget.id)
    }
    this.expanded = this.element.contains(document.activeElement)
  }

  opened() {
    this.expanded = true
  }

  closed(event) {
    this.expanded = Boolean(event.relatedTarget) && this.element.contains(event.relatedTarget)
  }

  // Escape: drop the focus, which is what closes a focus-within dropdown.
  close() {
    const active = document.activeElement
    if (active && this.element.contains(active)) active.blur()
    this.expanded = false
  }

  // Mouse only. `remember` runs on mousedown, before the browser moves the
  // focus; `toggle` then opens a closed menu (Safari needs the explicit
  // focus) or closes an open one (Chrome keeps the focus on a re-click, which
  // would leave the menu stuck open).
  remember() {
    this.wasOpen = this.expanded
  }

  toggle(event) {
    if (event.detail === 0) return // keyboard: the focus already opened it
    if (this.wasOpen) this.close()
    else this.triggerTarget.focus()
  }

  get expanded() {
    return this.triggerTarget.getAttribute("aria-expanded") === "true"
  }

  set expanded(value) {
    this.triggerTarget.setAttribute("aria-expanded", String(value))
  }
}
